import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getLowStockItems, LowStockItem } from '../../services/stockService';
import { AlertTriangle, X, Package, TrendingDown, ExternalLink } from 'lucide-react';

interface LowStockAlertProps {
  onRefresh?: () => void;
  compact?: boolean;
  maxItems?: number;
}

const LowStockAlert: React.FC<LowStockAlertProps> = ({ 
  onRefresh, 
  compact = false,
  maxItems = 5 
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadLowStockItems();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadLowStockItems, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadLowStockItems = async () => {
    setLoading(true);
    try {
      const branchId = currentUser?.role === 'owner' ? undefined : currentUser?.branch_id;
      const items = await getLowStockItems(branchId);
      setLowStockItems(items);
    } catch (err) {
      console.error('Failed to load low stock items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadLowStockItems();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Don't show if dismissed or no low stock items
  if (dismissed || lowStockItems.length === 0) {
    return null;
  }

  // Compact version (for dashboard widget)
  if (compact) {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
            <h3 className="font-semibold text-orange-900">Low Stock Alert</h3>
          </div>
          <span className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full">
            {lowStockItems.length}
          </span>
        </div>
        <div className="space-y-1">
          {lowStockItems.slice(0, maxItems).map((item) => (
            <div key={`${item.item_id}-${item.branch_id}`} className="text-sm text-orange-800">
              <span className="font-medium">{item.name}</span>
              <span className="text-orange-600 ml-2">
                ({item.current_qty}/{item.min_stock})
              </span>
            </div>
          ))}
          {lowStockItems.length > maxItems && (
            <div className="text-sm text-orange-600 font-medium">
              +{lowStockItems.length - maxItems} more items
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full banner version
  return (
    <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 rounded-lg shadow-md mb-6 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-bold text-orange-900">
                  Low Stock Alert
                </h3>
                <span className="bg-orange-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {lowStockItems.length} Items
                </span>
              </div>
              <p className="text-orange-800 mb-3">
                {lowStockItems.length === 1 
                  ? 'Ada 1 item yang stoknya di bawah minimum'
                  : `Ada ${lowStockItems.length} item yang stoknya di bawah minimum`}
              </p>

              {/* Quick Summary */}
              {!showDetails && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
                  {lowStockItems.slice(0, 6).map((item) => (
                    <div
                      key={`${item.item_id}-${item.branch_id}`}
                      className="bg-white rounded p-2 border border-orange-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {item.name}
                          </div>
                          <div className="text-xs text-gray-600">
                            {item.sku} • {item.branch_code}
                          </div>
                        </div>
                        <div className="ml-2 text-right">
                          <div className="text-sm font-bold text-orange-600">
                            {item.current_qty}
                          </div>
                          <div className="text-xs text-gray-500">
                            / {item.min_stock}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Detailed List */}
              {showDetails && (
                <div className="bg-white rounded-lg border border-orange-200 max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-orange-200">
                    <thead className="bg-orange-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-orange-900">
                          Item
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-orange-900">
                          SKU
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-orange-900">
                          Branch
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-orange-900">
                          Current
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-orange-900">
                          Min
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-orange-900">
                          Shortage
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-100">
                      {lowStockItems.map((item) => (
                        <tr key={`${item.item_id}-${item.branch_id}`} className="hover:bg-orange-50">
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.name}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.sku}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.branch_code}
                          </td>
                          <td className="px-4 py-2 text-sm text-right">
                            <span className={`font-semibold ${
                              item.current_qty === 0 ? 'text-red-600' : 'text-orange-600'
                            }`}>
                              {item.current_qty}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-600">
                            {item.min_stock}
                          </td>
                          <td className="px-4 py-2 text-sm text-right">
                            <span className="font-semibold text-red-600">
                              -{item.shortage}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-3 mt-3">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-orange-700 hover:text-orange-900 font-medium flex items-center"
                >
                  {showDetails ? (
                    <>
                      <Package className="w-4 h-4 mr-1" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View All Details
                      {lowStockItems.length > 6 && ` (${lowStockItems.length - 6} more)`}
                    </>
                  )}
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="text-sm text-orange-700 hover:text-orange-900 font-medium disabled:opacity-50"
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={() => setDismissed(true)}
            className="ml-4 text-orange-400 hover:text-orange-600 transition-colors"
            title="Dismiss alert"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Warning Strip */}
      {lowStockItems.some(item => item.current_qty === 0) && (
        <div className="bg-red-600 px-4 py-2 flex items-center">
          <TrendingDown className="w-4 h-4 text-white mr-2" />
          <span className="text-sm text-white font-semibold">
            {lowStockItems.filter(item => item.current_qty === 0).length} item(s) out of stock!
          </span>
        </div>
      )}
    </div>
  );
};

export default LowStockAlert;
